"use client";

import { useState, useTransition } from "react";
import type { SpotTip, TipCategory } from "@/app/actions/tips";
import { submitTip, voteTip } from "@/app/actions/tips";
import { useSpotUser } from "@/app/_components/conditions/SpotUserProvider";
import { Markdown } from "@/app/_components/Markdown";

type Props = {
  spotId: string;
  spotName: string;
  guideMd: string | null;
  isAiGenerated: boolean;
  tips: SpotTip[];
};

const CATEGORIES: { value: TipCategory; label: string }[] = [
  { value: "tide", label: "Tide" },
  { value: "wind", label: "Wind" },
  { value: "parking", label: "Parking" },
  { value: "hazards", label: "Hazards" },
  { value: "crowd", label: "Crowd" },
  { value: "equipment", label: "Equipment" },
  { value: "local_knowledge", label: "Local knowledge" },
  { value: "other", label: "Other" },
];

const CATEGORY_LABELS: Record<TipCategory, string> = Object.fromEntries(
  CATEGORIES.map((c) => [c.value, c.label])
) as Record<TipCategory, string>;

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  return `${diffDays}d ago`;
}

export function SpotGuide({
  spotId,
  spotName,
  guideMd,
  isAiGenerated,
  tips: initialTips,
}: Props) {
  const { isLoggedIn } = useSpotUser();
  const [tips, setTips] = useState(initialTips);
  const [formOpen, setFormOpen] = useState(false);
  const [formCategory, setFormCategory] = useState<TipCategory | null>(null);
  const [formText, setFormText] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const [votingIds, setVotingIds] = useState<Set<string>>(new Set());

  const handleVote = (tipId: string) => {
    if (!isLoggedIn) return;
    if (votingIds.has(tipId)) return;

    setVotingIds((prev) => new Set(prev).add(tipId));

    // Optimistic update
    setTips((prev) =>
      prev.map((t) =>
        t.id === tipId
          ? {
              ...t,
              hasVoted: !t.hasVoted,
              upvotes: t.hasVoted ? t.upvotes - 1 : t.upvotes + 1,
            }
          : t
      )
    );

    startTransition(async () => {
      const result = await voteTip(tipId);
      if (result.success && result.newCount !== undefined) {
        setTips((prev) =>
          prev.map((t) =>
            t.id === tipId ? { ...t, upvotes: result.newCount! } : t
          )
        );
      } else if (!result.success) {
        // Revert optimistic update
        setTips((prev) =>
          prev.map((t) =>
            t.id === tipId
              ? {
                  ...t,
                  hasVoted: !t.hasVoted,
                  upvotes: t.hasVoted ? t.upvotes - 1 : t.upvotes + 1,
                }
              : t
          )
        );
      }
      setVotingIds((prev) => {
        const next = new Set(prev);
        next.delete(tipId);
        return next;
      });
    });
  };

  const handleAddTip = () => {
    if (!isLoggedIn) {
      setFormError(
        "Create a free account to share your local knowledge"
      );
      return;
    }
    setFormError(null);
    setFormOpen(true);
  };

  const handleSubmit = () => {
    if (!formCategory) {
      setFormError("Choose a category");
      return;
    }
    if (!formText.trim()) {
      setFormError("Tip text is required");
      return;
    }
    setFormError(null);

    startTransition(async () => {
      const result = await submitTip(spotId, formCategory, formText.trim());
      if (result.success) {
        setFormOpen(false);
        setFormCategory(null);
        setFormText("");
      } else {
        setFormError(result.error ?? "Something went wrong");
      }
    });
  };

  const handleCancel = () => {
    setFormOpen(false);
    setFormCategory(null);
    setFormText("");
    setFormError(null);
  };

  // Group tips by category
  const grouped = new Map<TipCategory, SpotTip[]>();
  for (const tip of tips) {
    const existing = grouped.get(tip.category);
    if (existing) {
      existing.push(tip);
    } else {
      grouped.set(tip.category, [tip]);
    }
  }

  const hasGuide = guideMd !== null;
  const hasTips = tips.length > 0;

  if (!hasGuide && !hasTips && !isLoggedIn) return null;

  return (
    <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
      {/* Header */}
      <h2 className="font-serif text-[20px] md:text-[24px] font-bold text-navy">
        Spot guide
      </h2>
      <p className="text-[13px] text-text-muted mt-1">
        {isAiGenerated
          ? "This guide was generated from conditions data. Know this spot? Submit your own tips below."
          : `Local knowledge and community tips for ${spotName}`}
      </p>

      {/* Guide content */}
      {hasGuide && (
        <div className="mt-5">
          <Markdown content={guideMd} />
        </div>
      )}

      {/* Community tips */}
      {(hasTips || isLoggedIn) && (
        <div className={hasGuide ? "mt-6 pt-6 border-t border-border-light" : "mt-5"}>
          <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-4">
            Community tips
          </h3>

          {hasTips ? (
            <div className="space-y-5">
              {Array.from(grouped.entries()).map(([category, categoryTips]) => (
                <div key={category}>
                  <span className="inline-block px-2.5 py-0.5 rounded-full bg-navy/5 text-navy text-[11px] font-semibold mb-2.5">
                    {CATEGORY_LABELS[category]}
                  </span>
                  <div className="space-y-2">
                    {categoryTips.map((tip) => (
                      <div
                        key={tip.id}
                        className={[
                          "flex gap-3 items-start rounded-[10px] px-3 py-2.5",
                          tip.upvotes >= 3 ? "bg-gold/[0.04]" : "",
                        ].join(" ")}
                      >
                        {/* Upvote button */}
                        <button
                          type="button"
                          onClick={() => handleVote(tip.id)}
                          disabled={!isLoggedIn || votingIds.has(tip.id)}
                          aria-label={
                            tip.hasVoted
                              ? `Remove upvote (${tip.upvotes})`
                              : `Upvote (${tip.upvotes})`
                          }
                          className={[
                            "shrink-0 flex flex-col items-center gap-0.5 pt-0.5 min-w-[28px] transition-colors",
                            !isLoggedIn
                              ? "opacity-50 cursor-default"
                              : tip.hasVoted
                                ? "text-gold"
                                : "text-text-muted hover:text-navy",
                          ].join(" ")}
                        >
                          <svg
                            width="14"
                            height="10"
                            viewBox="0 0 14 10"
                            fill="currentColor"
                            aria-hidden
                          >
                            <path d="M7 0L13.9282 10H0.0717969L7 0Z" />
                          </svg>
                          <span className="text-[11px] font-semibold tabular-nums">
                            {tip.upvotes}
                          </span>
                        </button>

                        {/* Tip content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-[13.5px] leading-[1.6] text-text-body">
                            {tip.tipText}
                          </p>
                          <p className="text-[11px] text-text-muted mt-1">
                            by {tip.userFirstName} &middot; {timeAgo(tip.createdAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-text-muted">
              No tips yet. Be the first to share your local knowledge.
            </p>
          )}

          {/* Add a tip */}
          {!formOpen && (
            <div className="mt-4">
              <button
                type="button"
                onClick={handleAddTip}
                className="px-4 py-2 rounded-full bg-navy text-white text-[12px] font-semibold hover:bg-navy-hover transition-colors min-h-[44px]"
              >
                Add a tip
              </button>
            </div>
          )}

          {formError && !formOpen && (
            <p className="text-[12px] text-red-600 mt-2">{formError}</p>
          )}

          {/* Tip submission form */}
          {formOpen && (
            <div className="mt-4 pt-4 border-t border-border-light space-y-4">
              {/* Category selector */}
              <div>
                <p className="text-[12px] font-semibold text-text-muted mb-2">
                  Category
                </p>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map(({ value, label }) => {
                    const isActive = formCategory === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setFormCategory(isActive ? null : value)
                        }
                        className={[
                          "rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-colors min-h-[44px]",
                          isActive
                            ? "bg-navy/5 text-navy border-navy/20 ring-1 ring-offset-1 ring-navy/30"
                            : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100",
                        ].join(" ")}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Text input */}
              <div>
                <p className="text-[12px] font-semibold text-text-muted mb-2">
                  Your tip
                </p>
                <input
                  type="text"
                  value={formText}
                  onChange={(e) => setFormText(e.target.value)}
                  maxLength={500}
                  placeholder="Share what you know about this spot..."
                  className="w-full border border-border-light rounded-lg px-3 py-2.5 text-[14px] focus:outline-none focus:ring-1 focus:ring-navy/30"
                />
                <p className="text-[11px] text-text-muted mt-1 text-right tabular-nums">
                  {formText.length}/500
                </p>
              </div>

              {formError && (
                <p className="text-[12px] text-red-600">{formError}</p>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isPending || !formCategory || !formText.trim()}
                  className="px-5 py-2.5 rounded-full bg-navy text-white text-[13px] font-semibold hover:bg-navy-hover disabled:opacity-50 transition-colors min-h-[44px]"
                >
                  {isPending ? "Submitting..." : "Submit tip"}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2.5 rounded-full border border-border-light text-[13px] font-semibold text-text-muted hover:bg-slate-50 transition-colors min-h-[44px]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
